/* IP / CIDR parsing for IP ACL policies (design eval F18).

   The old check was two regexes: it accepted 999.1.1.1/99 and rejected every
   IPv6 address. This parses properly: IPv4 octets 0-255 with no leading zeros
   (Go's net.ParseIP rejects them too), IPv4 prefix 0-32, IPv6 in full or `::`
   compressed form (with an optional dotted IPv4 tail) and IPv6 prefix 0-128.
   A bare address is accepted as a single host (/32 or /128). */

export type IPVersion = 4 | 6;

export interface ParsedIP {
    version: IPVersion;
    /** The address as an unsigned integer (32 or 128 bits). */
    value: bigint;
}

export interface ParsedCIDR extends ParsedIP {
    prefix: number;
    /** True when the input carried an explicit /prefix. */
    explicitPrefix: boolean;
}

const OCTET = /^(0|[1-9]\d{0,2})$/;
const HEXTET = /^[0-9a-fA-F]{1,4}$/;

function parseIPv4(s: string): bigint | null {
    const parts = s.split('.');
    if (parts.length !== 4) return null;
    let v = 0n;
    for (const p of parts) {
        if (!OCTET.test(p)) return null;
        const n = Number(p);
        if (n > 255) return null;
        v = (v << 8n) | BigInt(n);
    }
    return v;
}

function parseIPv6(s: string): bigint | null {
    if (s.length === 0 || s.includes(':::')) return null;
    const halves = s.split('::');
    if (halves.length > 2) return null;

    const toGroups = (part: string): number[] | null => {
        if (part === '') return [];
        const out: number[] = [];
        const pieces = part.split(':');
        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            // A dotted IPv4 tail (::ffff:192.0.2.1) is only legal as the last piece.
            if (i === pieces.length - 1 && piece.includes('.')) {
                const v4 = parseIPv4(piece);
                if (v4 === null) return null;
                out.push(Number(v4 >> 16n), Number(v4 & 0xffffn));
                continue;
            }
            if (!HEXTET.test(piece)) return null;
            out.push(parseInt(piece, 16));
        }
        return out;
    };

    const head = toGroups(halves[0]);
    if (head === null) return null;
    let groups: number[];
    if (halves.length === 2) {
        const tail = toGroups(halves[1]);
        if (tail === null) return null;
        const missing = 8 - head.length - tail.length;
        // `::` stands for at least one zero group.
        if (missing < 1) return null;
        groups = [...head, ...new Array(missing).fill(0), ...tail];
    } else {
        groups = head;
    }
    if (groups.length !== 8) return null;
    return groups.reduce((acc, g) => (acc << 16n) | BigInt(g), 0n);
}

/** Parse a bare IPv4 or IPv6 address. Returns null when it is not one. */
export function parseIP(input: string): ParsedIP | null {
    const s = input.trim();
    if (s.includes(':')) {
        const v = parseIPv6(s);
        return v === null ? null : { version: 6, value: v };
    }
    const v = parseIPv4(s);
    return v === null ? null : { version: 4, value: v };
}

/** Parse an address or address/prefix. Returns null when it is invalid. */
export function parseCIDR(input: string): ParsedCIDR | null {
    const s = input.trim();
    if (!s) return null;
    const slash = s.indexOf('/');
    const addr = slash === -1 ? s : s.slice(0, slash);
    const ip = parseIP(addr);
    if (!ip) return null;
    const max = ip.version === 4 ? 32 : 128;
    if (slash === -1) return { ...ip, prefix: max, explicitPrefix: false };
    const prefixText = s.slice(slash + 1);
    if (!/^(0|[1-9]\d{0,2})$/.test(prefixText)) return null;
    const prefix = Number(prefixText);
    if (prefix > max) return null;
    return { ...ip, prefix, explicitPrefix: true };
}

export function isValidCIDR(input: string): boolean {
    return parseCIDR(input) !== null;
}

/** Why a CIDR is invalid, in words a user can act on. Null when it is valid. */
export function cidrError(input: string): string | null {
    const s = input.trim();
    if (!s) return 'Enter an IP address or CIDR.';
    if (parseCIDR(s)) return null;
    const slash = s.indexOf('/');
    const addr = slash === -1 ? s : s.slice(0, slash);
    const ip = parseIP(addr);
    if (!ip) {
        return addr.includes(':')
            ? `"${addr}" is not a valid IPv6 address.`
            : `"${addr}" is not a valid IPv4 address (four numbers 0-255, e.g. 10.0.0.0).`;
    }
    return ip.version === 4
        ? `The prefix in "${s}" must be a whole number from 0 to 32.`
        : `The prefix in "${s}" must be a whole number from 0 to 128.`;
}

/** True when the CIDR has bits set below its prefix (10.0.0.1/8). Still
    valid; the server masks it, but it is usually a typo. */
export function hasHostBits(c: ParsedCIDR): boolean {
    const bits = c.version === 4 ? 32 : 128;
    const hostBits = BigInt(bits - c.prefix);
    const hostMask = (1n << hostBits) - 1n;
    return (c.value & hostMask) !== 0n;
}

/** Whether `ip` falls inside `cidr`. Different IP versions never match. */
export function cidrContains(cidr: ParsedCIDR, ip: ParsedIP): boolean {
    if (cidr.version !== ip.version) return false;
    const bits = BigInt(cidr.version === 4 ? 32 : 128);
    const shift = bits - BigInt(cidr.prefix);
    return (cidr.value >> shift) === (ip.value >> shift);
}

export type AclVerdict =
    | { decision: 'deny'; reason: 'denied'; rule: string }
    | { decision: 'allow'; reason: 'allowed'; rule: string }
    | { decision: 'allow'; reason: 'no-allow-list' }
    | { decision: 'deny'; reason: 'not-allowed' };

/** Evaluate an IP against allow/deny lists using the rules this page
    documents: deny is checked first; an empty allow list allows everything
    not denied; otherwise the IP must match an allow entry. This mirrors the
    page's own description of the semantics, not the server's code. */
export function evaluateAcl(ip: ParsedIP, allow: string[], deny: string[]): AclVerdict {
    for (const d of deny) {
        const c = parseCIDR(d);
        if (c && cidrContains(c, ip)) return { decision: 'deny', reason: 'denied', rule: d };
    }
    if (allow.length === 0) return { decision: 'allow', reason: 'no-allow-list' };
    for (const a of allow) {
        const c = parseCIDR(a);
        if (c && cidrContains(c, ip)) return { decision: 'allow', reason: 'allowed', rule: a };
    }
    return { decision: 'deny', reason: 'not-allowed' };
}
